'use client';

import { type ChatRefDoc } from '@/app/services/chat';
import { type BatchDocDetail, getDocDetails } from '@/app/services/session';
import {
  AbstractPreviewContent,
  type AbstractLocale,
} from '@/components/search/abstract-preview-content';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  canTranslateOriginalArticle,
  getTranslatableArticleUrl,
} from '@/lib/reference-article-url';
import {
  getAbstractPreviewLocales,
  getOriginalLanguageLabel,
} from '@/lib/abstract-language';
import { getReferenceDocId } from '@/lib/reference-doc-id';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import {
  ArrowLeftIcon,
  ExternalLink,
  LanguagesIcon,
  SquareArrowOutUpRight,
  XIcon,
  LoaderCircleIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

type Props = {
  current: ChatRefDoc;
  referenceIndex?: number;
  locale: AbstractLocale;
  onLocaleChange: (locale: AbstractLocale) => void;
  onClose: () => void;
  canGoBack?: boolean;
  onBack?: () => void;
  isShare?: boolean;
  detailHref?: string | null;
  className?: string;
};

export function ReferencePreviewPanel({
  current,
  referenceIndex,
  locale,
  onLocaleChange,
  onClose,
  canGoBack,
  onBack,
  isShare = false,
  detailHref,
  className,
}: Props) {
  const router = useRouter();
  const { loading: authLoading, triggerLogin, user } = useAuth();
  const docId = getReferenceDocId(current) || null;
  const [docDetail, setDocDetail] = useState<BatchDocDetail | null>(null);
  const [isHydratingDetail, setIsHydratingDetail] = useState(false);
  const [isTranslateNavigating, setIsTranslateNavigating] = useState(false);

  useEffect(() => {
    setDocDetail(null);
    if (!docId) {
      setIsHydratingDetail(false);
      return;
    }

    let cancelled = false;
    setIsHydratingDetail(true);
    getDocDetails([docId])
      .then(({ data }) => {
        if (cancelled) return;
        const doc = data.docs?.[0];
        if (!doc) return;
        setDocDetail(doc);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setIsHydratingDetail(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [docId]);

  const hydratedCurrent = useMemo(() => {
    if (!docDetail || docDetail.doc_id !== docId)
      return { ...current, doc_id: current.doc_id ?? docId };
    return {
      ...current,
      ...docDetail,
      doc_id: docDetail.doc_id ?? current.doc_id ?? docId,
      abstract: docDetail.abstract ?? current.abstract,
      publication: docDetail.publication ?? current.publication,
      publisher:
        docDetail.publication ?? docDetail.publisher ?? current.publisher,
      publisher_abbr:
        docDetail.publication_abbr ??
        docDetail.publisher_abbr ??
        current.publisher_abbr,
      pub_day:
        docDetail.pub_day == null ? current.pub_day : String(docDetail.pub_day),
      pub_language: docDetail.pub_language ?? current.pub_language,
      translation: docDetail.translation ?? current.translation,
    };
  }, [current, docDetail, docId]);

  const availableLocales = getAbstractPreviewLocales(hydratedCurrent);
  const originalLanguageLabel = getOriginalLanguageLabel(
    hydratedCurrent.pub_language,
  );
  const effectiveLocale: AbstractLocale = docId
    ? availableLocales.includes(locale)
      ? locale
      : availableLocales[0]
    : 'en';
  const sourceUrl = hydratedCurrent.link?.trim();
  const showFulltextActions =
    Boolean(sourceUrl) ||
    (!isShare && canTranslateOriginalArticle(hydratedCurrent));

  const handleOpenSource = () => {
    if (!sourceUrl) {
      toast.error('暂未找到来源链接');
      return;
    }
    window.open(sourceUrl, '_blank', 'noopener,noreferrer');
  };

  const handleTranslateFulltext = () => {
    setIsTranslateNavigating(true);
    let didStartNavigation = false;
    const targetUrl = getTranslatableArticleUrl(hydratedCurrent);
    if (!targetUrl) {
      toast.error('暂未找到可翻译的全文');
      setIsTranslateNavigating(false);
      return;
    }
    const target = `/translate/reference?url=${encodeURIComponent(targetUrl)}&referer=pubmed&title=${encodeURIComponent(hydratedCurrent.title ?? '')}`;
    try {
      if (user || authLoading) {
        didStartNavigation = true;
        router.push(target);
      } else if (triggerLogin) {
        triggerLogin(() => {
          router.push(target);
        });
      } else {
        didStartNavigation = true;
        router.push(target);
      }
    } finally {
      if (!didStartNavigation) {
        setIsTranslateNavigating(false);
      }
    }
  };
  const fulltextActions = showFulltextActions ? (
    <>
      {sourceUrl && (
        <Button
          size="sm"
          variant="outline"
          className="h-8 rounded-full px-3 text-xs"
          onClick={handleOpenSource}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          来源
        </Button>
      )}
      {!isShare && canTranslateOriginalArticle(hydratedCurrent) && (
        <Button
          size="sm"
          variant="brand"
          className="h-8 rounded-full px-3 text-xs"
          disabled={isTranslateNavigating}
          onClick={() => void handleTranslateFulltext()}
        >
          {isTranslateNavigating ? (
            <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <LanguagesIcon className="h-3.5 w-3.5" />
          )}
          {isTranslateNavigating ? '正在打开' : '翻译全文'}
        </Button>
      )}
    </>
  ) : null;

  return (
    <div
      tabIndex={-1}
      className={cn(
        'flex h-full min-w-0 flex-col overflow-hidden outline-none bg-sidebar',
        className,
      )}
    >
      <div className="flex items-center justify-between px-4 py-2 shrink-0">
        <div className="flex items-center gap-1">
          {canGoBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onBack}
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">摘要详情</h3>
            {typeof referenceIndex === 'number' && (
              <span className="text-xs font-normal text-muted-foreground">
                引用 [{referenceIndex + 1}]
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {docId && (
            <Tabs
              value={effectiveLocale}
              onValueChange={(value) => onLocaleChange(value as AbstractLocale)}
              className="mr-2"
            >
              <TabsList className="inline-flex h-9 w-auto gap-1 rounded-full bg-muted p-1">
                {availableLocales.includes('zh') && (
                  <TabsTrigger
                    value="zh"
                    className="rounded-full px-4 text-sm data-[state=active]:bg-background"
                  >
                    中文
                  </TabsTrigger>
                )}
                {availableLocales.includes('en') && (
                  <TabsTrigger
                    value="en"
                    className="rounded-full px-4 text-sm data-[state=active]:bg-background"
                  >
                    {originalLanguageLabel}
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          )}
          {detailHref && (
            <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
              <Link href={detailHref} target="_blank">
                <SquareArrowOutUpRight className="h-4 w-4" />
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onClose}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain custom-scrollbar px-4 py-4">
        <div className="max-w-200 mx-auto">
          <AbstractPreviewContent
            current={hydratedCurrent}
            locale={effectiveLocale}
            fulltextActions={fulltextActions}
            deferAutoTranslate={isHydratingDetail}
          />
        </div>
      </div>
    </div>
  );
}
